import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { buildApiUrl } from '../../utils/apiSetup';

const MySalarySlips = () => {
  const { user } = useAuth();
  const [salarySlips, setSalarySlips] = useState([]);
  const [selectedSlip, setSelectedSlip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const formatCurrency = (value) =>
    `₹${Number(value ?? 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  useEffect(() => {
    fetchSalarySlips();
  }, [yearFilter]);

  const fetchSalarySlips = async () => {
    try {
      const url = yearFilter
        ? `/api/v1/benfish-payroll/slips/my-slips?year=${yearFilter}`
        : "/api/v1/benfish-payroll/slips/my-slips";

      const response = await fetch(buildApiUrl(url), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSalarySlips(data);
      } else {
        setError("Failed to fetch salary slips");
      }
    } catch (error) {
      console.error("Error fetching salary slips:", error);
      setError("Network error occurred");
    } finally {
      setLoading(false);
    }
  };

  const fetchSlipDetails = async (slipId) => {
    try {
      const response = await fetch(buildApiUrl(`/api/v1/benfish-payroll/slips/${slipId}`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedSlip(data);
      }
    } catch (error) {
      console.error("Error fetching slip details:", error);
    }
  };

  const getMonthName = (month) => {
    return new Date(0, month - 1).toLocaleString("default", { month: "long" });
  };

  const handleDownloadPDF = async (slip) => {
    try {
      const response = await fetch(buildApiUrl(`/api/v1/reports/wage-slip-pdf?month=${slip.payroll_month}&year=${slip.payroll_year}&emp_code=${user?.employee_code}`),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Wage_Slip_${slip.payroll_month}_${slip.payroll_year}_${user?.employee_code}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setError("Failed to download salary slip");
      }
    } catch (error) {
      console.error("Error downloading PDF:", error);
      setError("Network error occurred while downloading");
    }
  };

  const SalarySlipCard = ({ slip }) => (
    <div
      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
        selectedSlip?.id === slip.id
          ? "border-blue-500 bg-blue-50"
          : "border-gray-200 hover:border-gray-300 bg-white"
      }`}
      onClick={() => fetchSlipDetails(slip.id)}
    >
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="font-medium text-gray-900">
            {getMonthName(slip.payroll_month)} {slip.payroll_year}
          </h3>
          <p className="text-sm text-gray-600">Slip #{slip.slip_number}</p>
        </div>
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            slip.status === "generated"
              ? "bg-green-100 text-green-800"
              : slip.status === "approved"
                ? "bg-blue-100 text-blue-800"
                : "bg-gray-100 text-gray-800"
          }`}
        >
          {slip.status.charAt(0).toUpperCase() + slip.status.slice(1)}
        </span>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Gross Earnings:</span>
          <span className="font-medium">
            ₹{slip.total_earnings.toLocaleString()}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Deductions:</span>
          <span className="font-medium">
            ₹{slip.total_deductions.toLocaleString()}
          </span>
        </div>
        {(slip.loan_principal > 0 || slip.loan_interest > 0) && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Loan Recovery:</span>
            <span className="font-medium text-orange-600">
              ₹
              {(
                (slip.loan_principal || 0) + (slip.loan_interest || 0)
              ).toLocaleString()}
            </span>
          </div>
        )}
        <div className="flex justify-between text-sm font-bold border-t pt-2">
          <span>Net Pay:</span>
          <span className="text-green-600">
            ₹{slip.net_pay.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );

  const SalarySlipDetails = ({ slip }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Salary Slip</h2>
          <p className="text-gray-600">
            {getMonthName(slip.payroll_month)} {slip.payroll_year}
          </p>
        </div>
        <button
          onClick={() => setSelectedSlip(null)}
          className="text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>
      </div>

      {/* Employee Info */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Employee Name:</span>
            <div className="font-medium">
              {slip.employee_name || user?.employee_name}
            </div>
          </div>
          <div>
            <span className="text-gray-600">Employee ID:</span>
            <div className="font-medium">
              {slip.employee_code || user?.employee_code}
            </div>
          </div>
          <div>
            <span className="text-gray-600">Slip Number:</span>
            <div className="font-medium">{slip.slip_number}</div>
          </div>
          <div>
            <span className="text-gray-600">Working Days:</span>
            <div className="font-medium">{slip.working_days || 30}</div>
          </div>
        </div>
      </div>

      {/* Attendance Summary */}
      {slip.present_days && (
        <div className="mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">
            Attendance Summary
          </h3>
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="font-bold text-green-700">
                {slip.present_days}
              </div>
              <div className="text-green-600">Present</div>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <div className="font-bold text-red-700">
                {slip.absent_days || 0}
              </div>
              <div className="text-red-600">Absent</div>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="font-bold text-blue-700">
                {slip.leave_days || 0}
              </div>
              <div className="text-blue-600">Leave</div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="font-bold text-gray-700">
                {slip.working_days || 30}
              </div>
              <div className="text-gray-600">Total Days</div>
            </div>
          </div>
        </div>
      )}

      {/* Earnings and Deductions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Earnings */}
        <div>
          <h3 className="font-semibold text-gray-900 mb-3">Earnings</h3>
          <div className="space-y-2">
            {slip.basic > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Basic Salary</span>
                <span className="font-medium">
                  ₹{slip.basic.toLocaleString()}
                </span>
              </div>
            )}
            {slip.hra > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">House Rent Allowance</span>
                <span className="font-medium">
                  ₹{slip.hra.toLocaleString()}
                </span>
              </div>
            )}
            {slip.da > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Dearness Allowance</span>
                <span className="font-medium">₹{slip.da.toLocaleString()}</span>
              </div>
            )}
            {(slip.tpt || 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Transport Allowance (TPT)</span>
                <span className="font-medium">
                  ₹{slip.tpt.toLocaleString()}
                </span>
              </div>
            )}
            {slip.conveyance > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Conveyance</span>
                <span className="font-medium">
                  ₹{slip.conveyance.toLocaleString()}
                </span>
              </div>
            )}
            {slip.medical > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Medical Allowance</span>
                <span className="font-medium">
                  ₹{slip.medical.toLocaleString()}
                </span>
              </div>
            )}
            <div className="flex justify-between font-bold border-t pt-2">
              <span>Total Earnings</span>
              <span>₹{slip.total_earnings.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Deductions */}
        <div>
          <h3 className="font-semibold text-gray-900 mb-3">Deductions</h3>
          <div className="space-y-2">
            {slip.pf_amount > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">
                  PFSub (Mandatory PFS)
                </span>
                <span className="font-medium">
                  ₹{slip.pf_amount.toLocaleString()}
                </span>
              </div>
            )}
            {slip.vpf_deduction > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">
                  VPF (Voluntary PF)
                </span>
                <span className="font-medium">
                  ₹{slip.vpf_deduction.toLocaleString()}
                </span>
              </div>
            )}
            {((Number(slip.total_pf ?? ((slip.pf_amount || 0) + (slip.vpf_deduction || 0))) || 0) > 0) && (
              <div className="flex justify-between font-semibold">
                <span className="text-gray-700">Total PF</span>
                <span className="text-gray-900">
                  ₹{Number(slip.total_pf ?? ((slip.pf_amount || 0) + (slip.vpf_deduction || 0))).toLocaleString()}
                </span>
              </div>
            )}
            {slip.gis_amount > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">
                  {slip.gis_label || "GIS"}
                </span>
                <span className="font-medium">
                  ₹{slip.gis_amount.toLocaleString()}
                </span>
              </div>
            )}
            {slip.professional_tax > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Professional Tax</span>
                <span className="font-medium">
                  ₹{slip.professional_tax.toLocaleString()}
                </span>
              </div>
            )}
            {slip.income_tax > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Income Tax</span>
                <span className="font-medium">
                  ₹{slip.income_tax.toLocaleString()}
                </span>
              </div>
            )}
            {slip.loan_principal > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Loan Principal Recovery</span>
                <span className="font-medium text-orange-600">
                  ₹{slip.loan_principal.toLocaleString()}
                </span>
              </div>
            )}
            {slip.loan_interest > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Loan Interest</span>
                <span className="font-medium text-orange-600">
                  ₹{slip.loan_interest.toLocaleString()}
                </span>
              </div>
            )}
            {slip.advance_deduction > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Advance Recovery</span>
                <span className="font-medium">
                  ₹{slip.advance_deduction.toLocaleString()}
                </span>
              </div>
            )}
            {slip.penalty_deduction > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Penalty Deduction</span>
                <span className="font-medium text-red-600">
                  ₹{slip.penalty_deduction.toLocaleString()}
                </span>
              </div>
            )}
            {Array.isArray(slip.penalty_breakdown) &&
              slip.penalty_breakdown.length > 0 && (
                <div className="rounded-lg border border-red-100 bg-red-50 p-3">
                  <p className="text-xs font-semibold text-red-700 uppercase">
                    Penalty Breakdown
                  </p>
                  <div className="mt-2 space-y-1">
                    {slip.penalty_breakdown.map((item) => (
                      <div
                        key={`${item.penalty_rule_id}-${item.assignment_id || item.name}`}
                        className="flex justify-between text-sm"
                      >
                        <span className="text-gray-700">{item.name}</span>
                        <span className="font-medium text-red-700">
                          ₹{Number(item.amount || 0).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            <div className="flex justify-between font-bold border-t pt-2">
              <span>Total Deductions</span>
              <span>₹{slip.total_deductions.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Net Pay */}
      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex justify-between items-center">
          <span className="text-lg font-semibold text-green-800">Net Pay</span>
          <span className="text-2xl font-bold text-green-700">
            ₹{slip.net_pay.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-end mt-6">
        <button
          onClick={() => handleDownloadPDF(slip)}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
        >
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          Download PDF
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Salary Slips</h1>
        <select
          value={yearFilter}
          onChange={(e) => setYearFilter(parseInt(e.target.value))}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value={new Date().getFullYear()}>
            {new Date().getFullYear()}
          </option>
          <option value={new Date().getFullYear() - 1}>
            {new Date().getFullYear() - 1}
          </option>
          <option value={new Date().getFullYear() - 2}>
            {new Date().getFullYear() - 2}
          </option>
        </select>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Salary Slips List */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Salary Slips ({salarySlips.length})
          </h2>

          <div className="space-y-4">
            {salarySlips.length === 0 ? (
              <div className="p-8 bg-gray-50 border border-gray-200 rounded-lg text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p className="text-gray-500 mt-2">
                  No salary slips found for {yearFilter}
                </p>
              </div>
            ) : (
              salarySlips.map((slip) => (
                <SalarySlipCard key={slip.id} slip={slip} />
              ))
            )}
          </div>
        </div>

        {/* Salary Slip Details */}
        <div>
          {selectedSlip ? (
            <SalarySlipDetails slip={selectedSlip} />
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              <p className="text-gray-500 mt-2">
                Select a salary slip to view details
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MySalarySlips;
